import { GameServices } from "game/GameServices";
import { View } from "@westpierstudio/luma/mvc";
import { Tween } from "@westpierstudio/luma/createjs";
import { PayRow } from "servertypes/gameevents/OpenGame";
import { WinDisplayConfig } from "./WinlinesModel";
import { ReelsLayout } from "components/reels/ReelsModel";
import { PayLineWin } from "servertypes/SharedTypes";
import { getRectTexture } from "utils/Misc";
import { Signal } from "type-signals";
import { times } from "lodash";
import { Sprite, Container, SCALE_MODES, Text, NineSlicePlane } from "pixi.js";
import { AdvancedText } from "@westpierstudio/luma/utils/advancedtext";

class WinLineSignals {
    winLineComplete = new Signal();
    scatterLineComplete = new Signal<(symbols: {column: number, row: number}[], bonusComplete: boolean, value: number | null, loopIndex: number | null) => void>();
}

export class WinLines extends View {
    private readonly scatterWinBoxIndex = this.services.resources.getSetting(`winlines`).scatterWinBoxIndex;
    private spec!: Record<string, any>;
    private betlines!: {indices: number[], sprite: Sprite}[];
    private winBoxContainers!: Container[][];
    private winBoxTabs!: NineSlicePlane[];
    private reelsLayout!: ReelsLayout;
    private winDisplayConfig!: WinDisplayConfig;
    private haveSetup = false;
    private lineValue!: Text | AdvancedText;
    signals = new WinLineSignals();

    constructor(protected override readonly services: GameServices) {
        super(services);
    }

    setup(paylines: PayRow[][], reelsLayout: ReelsLayout): void {

        if (!this.haveSetup) {

            const {factory, resources} = this.services;

            this.spec = resources.getSpec(`winlines`);
            factory.applySpec(this, this.spec);

            this.setupBetLines(paylines);
            this.setupWinBoxes(reelsLayout, paylines.length);

            this.lineValue = factory.createText(``, this.spec.lineValue);

            this.reelsLayout = reelsLayout;
            this.haveSetup = true;
        }
    }

    setWinDisplayConfig(config: WinDisplayConfig): void {
        this.winDisplayConfig = config;
    }

    showWinLine(win: PayLineWin, timeBetweenWinsActive: boolean): void {

        const {paylineIndex, pattern} = win;
        const {positions, symbolSize} = this.reelsLayout;
        const {winBoxLineWidth} = this.spec;

        const indices = this.betlines[paylineIndex].indices;
        let symbolIndex, reelIndex, winBox;

        for (const value of pattern) {

            reelIndex = value.column;
            symbolIndex = indices[reelIndex];

            winBox = this.winBoxContainers[reelIndex][win.paylineIndex];
            winBox.y = positions[reelIndex].y + (symbolIndex * symbolSize.height) - winBoxLineWidth/2;
            winBox.visible = true;

            if (reelIndex === 0) {

                const winBoxTab = this.winBoxTabs[win.paylineIndex];
                this.services.factory.applySpec(winBoxTab, this.spec.winBoxTab);
                const maxWidth = winBoxTab.width - this.spec.lineValueMinimumPadding;

                this.lineValue.scale.set(1, 1);

                this.lineValue.text = this.services.currency.formatCoins(
                    win.moneyWon,
                    {useSubUnits: true, group: true, addTrailingZeros: true, trimIntegers: false}
                );

                if (this.lineValue.width > maxWidth) {

                    const ratio = maxWidth/this.lineValue.width;
                    this.lineValue.scale.set(ratio, ratio);
                }
                this.services.factory.applySpecAndParent(this.lineValue, this.spec.lineValueDefaulltPosition, winBoxTab);
                winBoxTab.visible = true;
            }
        }

        const betlineSprite =  this.betlines[paylineIndex].sprite;
        const maskSprite = this.setbetlineMask(win, indices, betlineSprite);
        betlineSprite.visible = true;

        const tween = Tween.get(this);

        tween.wait(this.winDisplayConfig.displayWinTime);
        tween.call(() => {
            this.hideAllWinBoxes();
            this.hideAllWinLines();
            maskSprite.destroy({children: true, texture: true, baseTexture: true});
        });

        if (timeBetweenWinsActive) {
            tween.wait(this.winDisplayConfig.timeBetweenWins);
        }

        tween.call(() => {
            this.signals.winLineComplete.dispatch();
        });
    }

    showScatterWin(symbols: {column: number, row: number}[], bonusComplete: boolean, value: number | null, loopIndex: null | number): void {

        const { resources, factory } = this.services;
        const {winBoxLineWidth} = this.spec;
        const {positions, symbolSize} = this.reelsLayout;
        const winBoxTab = this.winBoxTabs[this.scatterWinBoxIndex];

        const shouldDisplayValue = loopIndex !== null && value !== null && loopIndex % 2 === 0;

        const winBoxTabSpec = shouldDisplayValue ? this.spec.winBoxTabScatterValue: this.spec.winBoxTabScatter;
        const lineValuePositionSpec = shouldDisplayValue ? this.spec.lineValueScatterPosition : this.spec.lineScatterCompletePosition;
        const maxWidthPadding = shouldDisplayValue ? this.spec.lineValueMinimumPadding : this.spec.lineValueScatterMinimumPadding;

        factory.applySpec(winBoxTab, winBoxTabSpec);
        winBoxTab.visible = false;
        this.lineValue.text = shouldDisplayValue
            ? this.lineValue.text = this.services.currency.formatCoins(value, {useSubUnits: true, group: true, addTrailingZeros: true, trimIntegers: false})
            : resources.getMessage(`winBoxTabBonusComplete`);

        for (const value of symbols) {

            const winBox = this.winBoxContainers[value.column][this.scatterWinBoxIndex];
            winBox.y = positions[value.column].y + (value.row * symbolSize.height) - winBoxLineWidth/2,
            winBox.visible = true;

            if (value.column === 0 && bonusComplete) {
                const winBoxTab = this.winBoxTabs[this.scatterWinBoxIndex];

                this.lineValue.updateText(true);
                this.lineValue.scale.set(1, 1);
                const maxWidth = winBoxTab.width - maxWidthPadding;
                if (this.lineValue.width > maxWidth) {
                    const ratio = maxWidth/this.lineValue.width;
                    this.lineValue.scale.set(ratio, ratio);
                }

                factory.applySpecAndParent(this.lineValue, lineValuePositionSpec, winBoxTab);
                winBoxTab.visible = true;
            }
        }

        Tween
            .get(this, {override: true})
            .wait(this.winDisplayConfig.displayWinTime)
            .call(() => {
                this.hideAllWinBoxes();
                this.hideAllWinLines();
            })
            .wait(this.winDisplayConfig.timeBetweenWins)
            .call(() => this.signals.scatterLineComplete.dispatch(symbols, bonusComplete, value, loopIndex));
    }

    hideAllWinLines(): void {

        for (const entry of Object.values(this.betlines)) {
            entry.sprite.visible = false;
        }
    }

    hideAllWinBoxes(): void {

        for (const set of this.winBoxContainers) {
            for (const box of set) {

                if (box) {
                    box.visible = false;
                }
            }
        }
    }

    override reset(): void {

        if (this.haveSetup) {

            Tween.removeTweens(this);

            this.hideAllWinBoxes();
            this.hideAllWinLines();
        }
    }

    private setupBetLines(paylines: PayRow[][]): void {

        this.betlines = [];
        let sprite;

        for (const [i, entry] of paylines.entries()) {

            sprite = this.services.factory.createSprite(this.spec.slices[`line${i+1}`], this);
            sprite.visible = false;
            this.betlines.push({indices: entry, sprite});
        }
    }

    private setupWinBoxes(reelsLayout: ReelsLayout, paylineCount: number): void {

        const {positions, reelCount} = reelsLayout;
        const {factory} = this.services;

        this.winBoxContainers = times(reelsLayout.reelCount, () => []);
        this.winBoxTabs = [];

        for (let reelIndex=0; reelIndex<reelCount; reelIndex++) {
            for (let payLineIndex=0; payLineIndex<paylineCount; payLineIndex++) {
                this.setupWinBoxForLine(reelIndex, payLineIndex, reelsLayout);
            }
        }

        if (!this.winBoxContainers[this.scatterWinBoxIndex]) {
            for (let reelIndex=0; reelIndex<reelCount; reelIndex++) {
                this.setupWinBoxForLine(reelIndex, this.scatterWinBoxIndex, reelsLayout);
            }
        }
    }

    private setupWinBoxForLine(reelIndex: number, payLineIndex: number, reelsLayout: ReelsLayout): void {

        const { factory } = this.services;
        const { positions } = reelsLayout;

        this.winBoxContainers[reelIndex][payLineIndex] = factory.createContainer(
            {
                x: positions[reelIndex].x,
                y: positions[reelIndex].y,
                anchor: 0.5,
                visible: false
            },
            this
        );

        factory.createSpriteFromTextureKey(`box${payLineIndex+1}`, {anchor: 0.5}, this.winBoxContainers[reelIndex][payLineIndex]);

        if (reelIndex === 0) {
            const texture = this.services.resources.getTexture(`winboxvalue${payLineIndex+1}`);
            const {left, right, top, bottom} = this.spec.winBoxTabSlice;
            const winBoxTab = new NineSlicePlane(texture, left, top, right, bottom);
            factory.applySpecAndParent(winBoxTab, this.spec.winBoxTab, this.winBoxContainers[reelIndex][payLineIndex], 0);
            this.winBoxTabs[payLineIndex] = winBoxTab;
        }
    }

     /*
        Sprite Mask:
            Pixels discarded based on red channel
            Black squares representing inside of win boxes drawn into larger white rectangle
    */
    setbetlineMask(win: PayLineWin, indices: number[], sprite: Sprite) {

        sprite.removeChildren();
        const {winBoxLineWidth} = this.spec;

        const {application, factory} = this.services;
        const {winBoxHeight, winBoxWidth} = this.spec;
        const {positions, reelCount, reelSpacing, symbolCount, symbolSize} = this.reelsLayout;

        const betlineOverhang = 5;
        const maskWidth =  reelCount * symbolSize.width + (reelCount-1) * reelSpacing + betlineOverhang;
        const maskHeight = symbolCount * symbolSize.height;
        const textureScale = 1;

        const maskContainer = factory.createContainer({});
        let reelIndex, symbolIndex, x, y;

        const maskRect = getRectTexture(this.services, maskWidth, maskHeight, 0xFFFFFF, textureScale);
        const spriteRect = getRectTexture(this.services, winBoxWidth, winBoxHeight, 0x000000, textureScale);

        const reelSprite = factory.createSpriteFromTexture(
            maskRect,
            {},
            maskContainer
        );

        const boxSprites = [];
        for (const value of win.pattern) {

            reelIndex = value.column;
            symbolIndex = indices[reelIndex];

            x = positions[reelIndex].x + betlineOverhang/2 + winBoxLineWidth/2,
            y = symbolSize.height * symbolIndex;

            boxSprites.push(factory.createSpriteFromTexture(
                spriteRect,
                {x, y},
                maskContainer
            ));
        }

        const maskTexture = application.renderer.generateTexture(maskContainer, {
            scaleMode: SCALE_MODES.NEAREST,
            resolution: textureScale
        });

        const maskSprite = factory.createSpriteFromTexture(maskTexture, {x: 0, y: -(sprite.y + winBoxLineWidth/2)});

        sprite.addChild(maskSprite);
        sprite.mask = maskSprite;

        // clean up after ourselves
        maskRect.destroy(true);
        spriteRect.destroy(true);
        reelSprite.destroy({baseTexture: true, texture: true, children: true});
        boxSprites.forEach(sprite => sprite.destroy({baseTexture: true, texture: true, children: true}));
        maskContainer.destroy({children: true, baseTexture: true, texture: true});

        return maskSprite;
    }
}